import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Download, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, RefreshCw, Receipt, Mail, MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { pdfUrl, api, getInvoice, invoicePdfUrl } from "@/lib/api";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 6 * 60 * 1000; // 6 minutes

const GENERATING_MESSAGES = [
  "Prüfe Geschäftsmodell und Branchen-Parameter…",
  "Gleiche regulatorische Kriterien und regionale Rahmenbedingungen ab…",
  "Scanne passende Fördertöpfe (KfW, Landesprogramme & Sonderzuschüsse)…",
  "Strukturiere finalen Gründio Report und generiere PDF-Briefing…",
];

const GENERATING_MESSAGES_BUSINESS = [
  "Prüfe Geschäftsmodell und Branchen-Parameter…",
  "Gleiche regulatorische Kriterien und regionale Rahmenbedingungen ab…",
  "Analysiere Marktposition & Wettbewerbsumfeld…",
  "Strukturiere finalen Gründio Report und generiere PDF-Briefing…",
];

export default function Report() {
  const { id } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("loading");
  const [msgIdx, setMsgIdx] = useState(0);
  const [invoice, setInvoice] = useState(null);
  const [audience, setAudience] = useState("gruender");
  const [supportId, setSupportId] = useState(null);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [freeCoupon, setFreeCoupon] = useState(false);
  const isBusiness = audience === "business";
  const generatingMessages = isBusiness ? GENERATING_MESSAGES_BUSINESS : GENERATING_MESSAGES;

  useEffect(() => {
    getInvoice(id).then(setInvoice).catch(() => setInvoice(null));
  }, [id]);

  useEffect(() => {
    if (status !== "generating") return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % generatingMessages.length), 2200);
    return () => clearInterval(t);
  }, [status]);

  useEffect(() => {
    let alive = true;
    const start = Date.now();

    async function tick() {
      if (!alive) return;
      try {
        const s = await api.get(`/report/${id}/status`).then((r) => r.data);
        if (!alive) return;
        if (s.audience) setAudience(s.audience);
        if (s.support_id) setSupportId(s.support_id);
        setEmailOptIn(!!s.email_opt_in);
        setFreeCoupon(!!s.free_coupon);

        if (!s.paid) {
          toast.error("Zahlung erforderlich.");
          nav(`/teaser/${id}`);
          return;
        }
        if (!s.details_completed && !s.report_ready) {
          nav(`/report-erstellen/${id}`);
          return;
        }
        if (s.report_ready) {
          setStatus("ready");
          return;
        }
        if (s.status === "failed") {
          setStatus("failed");
          return;
        }
        setStatus("generating");
        if (Date.now() - start > POLL_MAX_MS) {
          setStatus("failed");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        console.error(e);
        if (e?.response?.status === 404) {
          toast.error("Bericht nicht gefunden.");
          nav("/");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    }

    tick();
    return () => { alive = false; };
  }, [id, nav]);

  const retry = async () => {
    try {
      await api.post(`/report/${id}/regenerate`);
      setStatus("generating");
      toast.success("Generierung neu gestartet…");
    } catch {
      toast.error("Neustart fehlgeschlagen.");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--brand-solid)" }} />
      </div>
    );
  }

  if (status === "generating") {
    return (
      <div className={`min-h-screen flex items-center justify-center px-6 ${isBusiness ? "theme-petrol" : ""}`}>
        <div
          data-testid="report-generating"
          className="relative max-w-xl w-full rounded-3xl overflow-hidden shadow-2xl"
        >
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-64 w-64 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-64 w-64 rounded-full opacity-25 bg-emerald-400 blur-3xl" />
          <div className="relative px-8 py-10 text-white">
            <div className="flex items-center gap-3 mb-6">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-300" />
              <div className="text-xs uppercase tracking-widest text-emerald-300">
                Dein Report wird erstellt
              </div>
            </div>
            <div
              key={msgIdx}
              className="text-xl font-semibold mb-8 animate-fade-in-up"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {generatingMessages[msgIdx]}
            </div>
            <div className="space-y-3">
              <div className="shimmer-dark h-3" />
              <div className="shimmer-dark h-3 w-11/12" />
              <div className="shimmer-dark h-3 w-9/12" />
              <div className="shimmer-dark h-3 w-10/12" />
              <div className="shimmer-dark h-3 w-7/12" />
            </div>
            <p className="text-xs text-white/60 mt-8">
              Die vollständige KI-Erstbewertung (über 10 Seiten) dauert ca. 1–5 Minuten.
              Du kannst dieses Fenster offen lassen – sie steht anschließend als Download bereit
              und wird dir zusätzlich per E-Mail zugeschickt.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className={`min-h-screen flex items-center justify-center px-6 ${isBusiness ? "theme-petrol" : ""}`}>
        <div data-testid="report-outage-card" className="bg-white border border-amber-200 rounded-2xl p-8 max-w-md w-full shadow-lg">
          <div className="flex items-center gap-2 text-amber-700 mb-3">
            <AlertTriangle className="h-5 w-5" />
            <div className="font-semibold">Kurzfristige Störung – kein Problem</div>
          </div>
          <p className="text-sm text-slate-700 mb-4 leading-relaxed">
            Aktuell ist die KI-Erstellung stark ausgelastet bzw. kurzzeitig gestört. <b>Deine Zahlung ist gespeichert – du hast nichts umsonst bezahlt.</b>
          </p>
          <div className="flex items-start gap-2 text-sm text-slate-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-5">
            <Mail className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--brand-solid)" }} />
            <span>
              Wir versuchen die Fertigstellung <b>automatisch weiter</b>. Bei einer anhaltenden Störung ist deine Zahlung gesichert – melde dich mit deiner Support-ID bei uns, dann finden wir eine Lösung.
            </span>
          </div>
          {supportId && (
            <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center" data-testid="report-support-id-failed">
              <div className="text-xs uppercase tracking-widest text-slate-400">Deine Support-ID</div>
              <div className="text-lg font-bold text-slate-900 mt-0.5 select-all">{supportId}</div>
            </div>
          )}
          <Button
            data-testid="report-retry-btn"
            onClick={retry}
            variant="outline"
            className="w-full h-12 rounded-xl font-semibold"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Jetzt erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  // ready → download / success page (no inline report content)
  return (
    <div className={`min-h-screen bg-slate-50 ${isBusiness ? "theme-petrol" : ""}`}>
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="report-home-btn"
            onClick={() => nav("/")}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Startseite
          </button>
          <Logo size="sm" variant={isBusiness ? "business" : "founder"} />
          <Button
            data-testid="report-download-btn"
            asChild
            className="h-10 rounded-lg font-semibold brand-gradient-bg text-white hover:opacity-95"
          >
            <a href={pdfUrl(id)} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4 mr-2" /> PDF
            </a>
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div
          data-testid="report-ready-card"
          className="bg-white rounded-3xl border border-slate-200/70 shadow-sm p-6 sm:p-10 text-center"
        >
          <div className="flex justify-center mb-5">
            <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9" style={{ color: "var(--brand-solid)" }} />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Dein Report ist fertig.
          </h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Deine vollständige KI-Erstbewertung steht als gebrandetes PDF (inkl. Cover &amp; Inhaltsverzeichnis)
            zum Download bereit. <b>Bitte lade sie dir jetzt herunter</b> – aus Datenschutzgründen speichern wir
            Geschäftsidee und Report <b>nicht dauerhaft</b> (Download nur für begrenzte Zeit verfügbar).
          </p>

          <div className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
            <Mail className="h-4 w-4 shrink-0" style={{ color: "var(--brand-solid)" }} />
            <span data-testid="report-email-note">
              {freeCoupon
                ? (emailOptIn
                    ? "Wir haben dir den Report zusätzlich per E-Mail zugesendet."
                    : "Bitte lade den Report jetzt herunter – aus Datenschutzgründen speichern wir ihn nicht dauerhaft.")
                : (emailOptIn
                    ? "Wir haben dir den Report & deine Rechnung zusätzlich per E-Mail zugesendet."
                    : "Deine Rechnung haben wir dir per E-Mail zugesendet. Den Report bitte hier direkt herunterladen.")}
            </span>
          </div>

          <Button
            asChild
            className="mt-7 h-14 w-full rounded-xl font-semibold text-base brand-gradient-bg text-white hover:opacity-95"
          >
            <a href={pdfUrl(id)} target="_blank" rel="noreferrer" data-testid="report-download-main">
              <Download className="h-5 w-5 mr-2" /> Report als PDF herunterladen
            </a>
          </Button>

          {!freeCoupon && (invoice ? (
            <div className="mt-4 flex items-center justify-between gap-4 flex-wrap rounded-xl bg-white border border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Receipt className="h-4 w-4" style={{ color: "var(--brand-solid)" }} />
                <span><b>Rechnung {invoice.number}</b> · {invoice.date}</span>
              </div>
              <Button
                asChild
                data-testid="report-invoice-btn"
                variant="outline"
                className="h-10 rounded-lg font-medium"
              >
                <a href={invoicePdfUrl(id)} target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4 mr-2" /> Rechnung (PDF)
                </a>
              </Button>
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-between gap-4 flex-wrap rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Receipt className="h-4 w-4 text-amber-700" />
                <span>Rechnung noch nicht erstellt.</span>
              </div>
              <Button
                onClick={() => nav(`/invoice/${id}`)}
                data-testid="report-invoice-create-btn"
                variant="outline"
                className="h-10 rounded-lg font-medium border-amber-300 text-amber-900"
              >
                Rechnung erstellen
              </Button>
            </div>
          ))}
          {supportId && (
            <p className="mt-4 text-xs text-slate-400" data-testid="report-support-id">
              Support-ID: <span className="font-semibold text-slate-500 select-all">{supportId}</span> · bitte bei Rückfragen angeben
            </p>
          )}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <MessageSquareText className="h-5 w-5" style={{ color: "var(--brand-solid)" }} />
            <div className="text-sm text-slate-700">
              <b>Wie war deine Erfahrung?</b> Dein Feedback hilft uns weiter.
            </div>
          </div>
          <Button
            asChild
            data-testid="report-feedback-btn"
            variant="outline"
            className="h-10 rounded-lg font-medium"
          >
            <Link to={`/feedback/${id}`}>Feedback geben</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
